import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { Code } from 'src/app/core/model/dictionary';
import { Kno2Service } from 'src/app/core/service/kno2.service';
import { Observable } from 'rxjs';
import { TabStripComponent, SelectEvent } from '@progress/kendo-angular-layout';
import copyToClipboard from 'copy-to-clipboard';
import { AppConfigService } from 'src/app/core/service/app-config.service';

@Component({
  selector: 'app-kno2-search',
  templateUrl: './kno2-search.component.html',
  /*Only way to override for Kendo
  styleUrls: ['./directory-search-kno2.component.scss'],*/
  encapsulation: ViewEncapsulation.None,
  styles: [`
  #provider-search .k-grid-aria-root { height: 500px; font-size: small }
  .directAddress { font-size: smaller; color: #cf4526;}
  .reduced-custom {  margin-left: -25px; margin-right: -25px; }`
  ]
})
export class Kno2SearchComponent implements OnInit
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  @Input() public allowSelection = false;
  @Input() public singleSelectionOnly = false;
  @Input() public showOrganizationsTab = false;

  @Output() public selectProvider = new EventEmitter();

  public searchForm: any;
  public states: Code[] = [];
  public dataProviders: Observable<any>;
  public dataOrganizations: Observable<any>;
  public loading: boolean;
  public totalProvidersCount: number;
  public totalOrganizationsCount: number;
  public selectedProviders: number[] = [];
  public selectedOrganizations: number[] = [];
  public selectedTabIndex: number;
  public loaded = false;

  constructor(
    private fb: UntypedFormBuilder,
    private dictionaryService: DictionaryService,
    private kno2Service: Kno2Service,
    private appConfigService: AppConfigService
  )
  {
    this.dataProviders = kno2Service.providers;
    this.dataOrganizations = kno2Service.organizations;
  }

  public get firstName(): UntypedFormControl
  {
    return this.searchForm.get('firstName');
  }

  public get lastName(): UntypedFormControl
  {
    return this.searchForm.get('lastName');
  }

  public get organizationName(): UntypedFormControl
  {
    return this.searchForm.get('organizationName');
  }

  public get city(): UntypedFormControl
  {
    return this.searchForm.get('city');
  }

  public get state(): UntypedFormControl
  {
    return this.searchForm.get('state');
  }

  public get zip(): UntypedFormControl
  {
    return this.searchForm.get('zip');
  }

  @Input() public set dialogOpened(value: boolean)
  {
    if (value)
    {
      this.buildForm();
      this.resetSearch();

      // Set default tab
      if (this.tabstrip !== undefined)
      {
        this.tabstrip.selectTab(0);
      }
    }
  }

  public ngOnInit(): void
  {
    this.buildForm();
    this.selectedTabIndex = 0;
    this.clearSearchForm();

    // get list of states/provinces from Dictionary table
    this.dictionaryService.getCachedStatesOrProvinces().subscribe({
      next: (data) =>
      { this.states = data; }
    });
  }

  /**
   * Method called when the select button have been hit.
   * The method construct a selected array that will contains the list of selected providers.
   */
  public onSelectedProviders(): void
  {
    const selected = [];
    this.selectedProviders.forEach(x =>
    {
      this.dataProviders.subscribe({
        next: (providers) =>
        {
          if (providers && providers.length > 0)
          {
            // Mapping Kno2 search results to Channels format
            selected.push({
              directorySource: 'KNO2',
              firstName: providers[x].firstName,
              lastName: providers[x].lastName,
              middleName: providers[x].middleName,
              organization: (providers[x].organizationName ? providers[x].organizationName : providers[x].clinicName),
              directAddress: providers[x].directAddress,
              faxNumber: providers[x].fax,
              telephoneNumber: providers[x].phonePrimary,
              npi: providers[x].npi,
              practiceAddress: {
                streetLine: providers[x].standardizedAddressLine1,
                city: providers[x].standardizedCity,
                state: providers[x].standardizedState,
                postalCode: providers[x].standardizedZip
              }
            });
          }
        }
      });
    });

    this.selectedOrganizations.forEach(x =>
    {
      this.dataOrganizations.subscribe({
        next: (providers) =>
        {
          if (providers && providers.length > 0)
          {
            selected.push({
              directorySource: 'KNO2',
              firstName: providers[x].firstName ? providers[x].firstName : null,
              lastName: providers[x].lastName ? providers[x].lastName : null,
              middleName: providers[x].middleName ? providers[x].middleName : null,
              organization: (providers[x].organizationName ? providers[x].organizationName : providers[x].clinicName),
              directAddress: providers[x].directAddress ? providers[x].directAddress : null,
              faxNumber: providers[x].fax ? providers[x].fax : null,
              telephoneNumber: providers[x].phonePrimary ? providers[x].phonePrimary : null,
              npi: providers[x].npi ? providers[x].npi : null,
              practiceAddress: {
                streetLine: providers[x].standardizedAddressLine1 ? providers[x].standardizedAddressLine1 : null,
                city: providers[x].standardizedCity ? providers[x].standardizedCity : null,
                state: providers[x].standardizedState ? providers[x].standardizedState : null,
                postalCode: providers[x].standardizedZip ? providers[x].standardizedZip : null
              }
            });
          }
        }
      });
    });
    this.selectProvider.emit(selected);
  }

  /**
   * Check if clear button should be hidden.
   *
   * @returns boolean Will returns true if all the form property are empty or grid doesn't contains any data.
   */
  public clearHidden(): boolean
  {
    if (this.searchForm !== undefined)
    {
      return (this.firstName.value === null &&
        this.lastName.value === null &&
        this.organizationName.value === null &&
        this.city.value === null &&
        (this.state.value === null || this.state.value === undefined) &&
        this.zip.value === null &&
        (this.totalProvidersCount === undefined || this.totalProvidersCount === 0) &&
        (this.totalOrganizationsCount === undefined || this.totalOrganizationsCount === 0));
    }
    else
    {
      return false;
    }
  }

  /**
   * Searching Kno2 directory.
   *
   * @param reset When called from the Search button, reset will be true and will reset services page, count, etc.
   */
  public searchDirectory(reset: boolean): void
  {
    if (reset)
    {
      this.resetSearch();
    }

    this.loading = true;
    this.kno2Service.searchDirectory(this.selectedTabIndex, this.appConfigService.getConfig().pageSize,
      this.searchForm.value).subscribe({
        next: (data) =>
        {
          this.loading = false;
          if (this.totalProvidersCount === undefined)  // Need to set only once per query. Endless scrolling doesn't need count refresh.
          {
            this.totalProvidersCount = data.totalProvidersCount;
            this.totalOrganizationsCount = data.totalOrganizationsCount;
          }
        }
      });
  }

  /**
   * Clear the search form criteria and grid.
   */
  public clearSearchForm(): void
  {
    this.firstName.setValue(null);
    this.lastName.setValue(null);
    this.organizationName.setValue(null);
    this.city.setValue(null);
    this.state.setValue(null);
    this.zip.setValue(null);
    this.resetSearch();
  }

  /**
   * Return the provider count to display on the tab.
   *
   * @param count Count to display.
   */
  public getProviderCount(count: number): string
  {
    if (count !== undefined)
    {
      const max = 99;
      return count > max ? `(${max}+)` : `(${count})`;
    }
    else
    {
      return '';
    }
  }

  /**
   * Provide the full provider name to display.
   *
   * @param dataItem Current row dataItem.
   */
  public getProviderName(dataItem: any): string
  {
    return (dataItem !== undefined && dataItem !== null) ? `${dataItem.firstName} ${dataItem.lastName}` : '';
  }

  /**
   * Copy the direct address to clipboard.
   *
   * @param directAddress Direct address to copy.
   */
  public copyDirectAddress(directAddress: string): void
  {
    copyToClipboard(directAddress);
  }

  /**
   * Event occurs when the tab is changed. This is to keep the selected tab index.
   *
   * @param selectEvent Select tab.
   */
  public onTabSelect(selectEvent: SelectEvent): void
  {
    this.selectedTabIndex = selectEvent.index;
  }

  /**
   * Execute search on enter
   *
   * @param e Event
   */
  public keyDown(event: KeyboardEvent): void
  {
    if (event && event.key === 'Enter')
    {
      event.preventDefault();
      this.searchDirectory(true);
    }
  }

  public onSelectedKeysChange(e: any, propName: string): void
  {
    this[propName] = e;
    this.onSelectedProviders();
  }

  /**
   * Initialized model on open.
   *
   * @param insurance Insurance model to edit.
   */
  private buildForm()
  {
    // Set form
    this.searchForm = this.fb.group({
      firstName: [{ value: null, disabled: false }],
      lastName: [{ value: null, disabled: false }],
      organizationName: [{ value: null, disabled: false }],
      city: [{ value: null, disabled: false }],
      state: [{ value: null, disabled: false }],
      zip: [{ value: null, disabled: false }]
    });
    this.loaded = true;
  }

  /**
   * Reset search data, count and service.
   */
  private resetSearch(): void
  {
    this.selectedProviders = [];
    this.selectedOrganizations = [];
    this.loading = false;
    this.totalProvidersCount = undefined;
    this.totalOrganizationsCount = undefined;
    this.kno2Service.searchDirectoryReset();
    this.selectProvider.emit([]);
  }
}
